import React, { useState, useRef, useMemo, useEffect } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import {
	OrbitControls,
	Environment,
	Html,
	Line,
	useTexture,
} from "@react-three/drei";
import { Button, Modal, Form } from 'react-bootstrap';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useForm } from "react-hook-form";
import like from "../../components/picture/like (1).png";
import Dislike from "../../components/picture/like.png";
import * as THREE from "three";

import { useNavigate } from "react-router-dom";
import "./MyAdvertisements.scss";
import axios from "axios";
import APP_BASE_URL from "../../components/Api/globalApi";
import { io } from "socket.io-client";

// Simple Error Boundary to catch R3F crashes
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}
	static getDerivedStateFromError(error) {
		return { hasError: true };
	}
	componentDidCatch(error, errorInfo) {
		console.error("ErrorBoundary caught an error", error, errorInfo);
	}
	render() {
		if (this.state.hasError) {
			return this.props.fallback || null;
		}
		return this.props.children;
	}
}

// SafeText: replace drei/Text with an Html-based fallback to avoid customDepthMaterial issues
function SafeText({ children, position, fontSize = 0.25, color = '#000', anchorX = 'center', anchorY = 'middle', style = {}, ...rest }) {
	// Convert relative fontSize to px (approx)
	const px = Math.max(10, Math.round(fontSize * 16));
	const textAlign = anchorX === 'center' ? 'center' : anchorX === 'right' ? 'right' : 'left';

	return (
		<Html position={position} transform center={anchorX === 'center'} {...rest}>
			<div style={{ pointerEvents: 'none', fontSize: `${px}px`, color, textAlign, lineHeight: 1, whiteSpace: 'pre-wrap', ...style }}>
				{children}
			</div>
		</Html>
	);
}

function RegistrationForm({ onBack }) {
	const { register, handleSubmit, formState: { errors } } = useForm();
	const [submitting, setSubmitting] = useState(false);
	const navigate = useNavigate();

	const onSubmit = async (data) => {
		setSubmitting(true);
		try {
			const response = await axios.post(`${APP_BASE_URL}/no-registration/register`, data);
			if (response.data.result) {
				toast.success(response.data.message || "Registered successfully");
				setTimeout(() => {
					onBack(); // close form
					navigate("/user-points");
				}, 800);
			} else {
				toast.error(response.data.message || "Registration failed");
			}
		} catch (error) {
			console.error("Registration error:", error);
			toast.error(error.response?.data?.message || "An error occurred during registration");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="welcome-form">
			<h2 style={{ marginBottom: '1.5rem', color: '#00ffff' }}>Register Your Interest</h2>

			<div className="form-group mb-3 welcome-form__field">
				<input {...register("name")} placeholder="Name" className="form-control" />
			</div>

			<div className="form-group mb-3 welcome-form__field">
				<input {...register("email")} placeholder="Email" className="form-control" />
			</div>

			<div className="form-group mb-3 welcome-form__field">
				<input {...register("phonenumber", { required: "Phone number is required" })} placeholder="Phone Number" className="form-control" />
				{errors.phonenumber && <p className="welcome-form__error">{errors.phonenumber.message}</p>}
			</div>

			<div className="form-group mb-4 welcome-form__field">
				<input {...register("district")} placeholder="District" className="form-control" />
			</div>

			<div className="welcome-form__actions">
				<button type="button" onClick={onBack} className="btn btn-secondary" disabled={submitting}>Back</button>
				<button type="submit" className="btn btn-primary" disabled={submitting}>
					{submitting ? "Submitting..." : "Submit"}
				</button>
			</div>
		</form>
	);
}

function WelcomeParticleField({
	count = 900,
	spread = 34,
	height = 18,
	depth = 26,
	size = 0.05,
	drift = 0.015,
	opacity = 0.85,
}) {
	const pointsRef = useRef();
	const { positions, colors } = useMemo(() => {
		const particlePositions = new Float32Array(count * 3);
		const particleColors = new Float32Array(count * 3);
		const palette = ["#f7fbff", "#8ee3ff", "#9aa7ff", "#d5deff"].map(
			(color) => new THREE.Color(color)
		);

		for (let index = 0; index < count; index += 1) {
			const offset = index * 3;
			particlePositions[offset] = THREE.MathUtils.randFloatSpread(spread);
			particlePositions[offset + 1] = THREE.MathUtils.randFloatSpread(height);
			particlePositions[offset + 2] = THREE.MathUtils.randFloat(-depth, depth);

			const tint = palette[index % palette.length]
				.clone()
				.lerp(new THREE.Color("#ffffff"), Math.random() * 0.35);
			particleColors[offset] = tint.r;
			particleColors[offset + 1] = tint.g;
			particleColors[offset + 2] = tint.b;
		}

		return { positions: particlePositions, colors: particleColors };
	}, [count, depth, height, spread]);

	useFrame((state) => {
		if (!pointsRef.current) {
			return;
		}

		const elapsed = state.clock.elapsedTime;
		pointsRef.current.rotation.y = elapsed * drift;
		pointsRef.current.rotation.x = Math.sin(elapsed * 0.12) * 0.05;
		pointsRef.current.position.z = Math.sin(elapsed * 0.18) * 0.35;
	});

	return (
		<points ref={pointsRef}>
			<bufferGeometry>
				<bufferAttribute
					attach="attributes-position"
					count={positions.length / 3}
					array={positions}
					itemSize={3}
				/>
				<bufferAttribute
					attach="attributes-color"
					count={colors.length / 3}
					array={colors}
					itemSize={3}
				/>
			</bufferGeometry>
			<pointsMaterial
				size={size}
				sizeAttenuation
				vertexColors
				transparent
				opacity={opacity}
				depthWrite={false}
				blending={THREE.AdditiveBlending}
			/>
		</points>
	);
}

function MarketGridFloor() {
	return (
		<group position={[0, -3.65, -4.8]} rotation={[-1.18, 0, 0]}>
			<mesh>
				<planeGeometry args={[30, 12, 34, 18]} />
				<meshBasicMaterial
					color="#0e5fae"
					wireframe
					transparent
					opacity={0.42}
				/>
			</mesh>
			<mesh position={[0, 0, -0.02]}>
				<planeGeometry args={[30, 12]} />
				<meshBasicMaterial
					color="#061325"
					transparent
					opacity={0.56}
				/>
			</mesh>
		</group>
	);
}

function MarketCandles() {
	const groupRef = useRef();
	const candles = useMemo(() => {
		const values = [
			0.15, 0.52, 0.66, 0.38, 0.22, 0.41, 0.33, 0.58, 0.47, 0.62, 0.81,
			0.56, 0.44, 0.71, 0.88, 0.74, 0.63, 0.95, 0.7, 0.54, 0.83, 0.98,
		];

		return values.map((value, index) => {
			const x = -10.2 + index * 0.95;
			const color = index % 3 === 1 ? "#ffbf54" : "#1fe4ff";
			const bodyHeight = 0.9 + value * 2.7;
			const bodyY = -1.95 + value * 4.3;
			const wickHeight = bodyHeight + 1 + (index % 4) * 0.22;

			return {
				x,
				color,
				bodyHeight,
				bodyY,
				wickHeight,
				width: index % 5 === 0 ? 0.16 : 0.13,
			};
		});
	}, []);

	useFrame((state) => {
		if (!groupRef.current) {
			return;
		}

		groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.35) * 0.08;
	});

	return (
		<group ref={groupRef} position={[0, 0, -3.8]}>
			{candles.map((candle, index) => (
				<group key={`candle-${index}`} position={[candle.x, candle.bodyY, 0]}>
					<mesh position={[0, 0, -0.08]}>
						<boxGeometry args={[candle.width * 2.4, candle.bodyHeight * 1.1, 0.05]} />
						<meshBasicMaterial
							color={candle.color}
							transparent
							opacity={0.14}
							blending={THREE.AdditiveBlending}
						/>
					</mesh>
					<mesh position={[0, 0, 0]}>
						<boxGeometry args={[candle.width, candle.bodyHeight, 0.16]} />
						<meshBasicMaterial
							color={candle.color}
							transparent
							opacity={0.96}
						/>
					</mesh>
					<mesh position={[0, 0, -0.03]}>
						<cylinderGeometry args={[0.028, 0.028, candle.wickHeight, 8]} />
						<meshBasicMaterial
							color={candle.color}
							transparent
							opacity={0.92}
						/>
					</mesh>
				</group>
			))}
		</group>
	);
}

function MarketTrendLines() {
	const whitePoints = useMemo(
		() => [
			[-12, -0.1, -2.3],
			[-9.7, 2.8, -2.3],
			[-7.2, 1.05, -2.3],
			[-5.35, 1.82, -2.3],
			[-3.05, 0.95, -2.3],
			[-0.6, 2.55, -2.3],
			[1.2, 1.45, -2.3],
			[3.8, 3.48, -2.3],
			[6.25, 1.85, -2.3],
			[8.85, 4.05, -2.3],
		],
		[]
	);
	const bluePoints = useMemo(
		() => [
			[-11.2, -1.7, -2.7],
			[-8.4, -0.6, -2.7],
			[-5.8, 0.5, -2.7],
			[-3.1, 0.18, -2.7],
			[-0.8, 1.35, -2.7],
			[1.55, 1.08, -2.7],
			[4.2, 2.42, -2.7],
			[6.9, 2.1, -2.7],
			[9.8, 3.58, -2.7],
		],
		[]
	);

	return (
		<group>
			<Line
				points={whitePoints}
				color="#f9fcff"
				lineWidth={1.7}
				transparent
				opacity={0.95}
			/>
			<Line
				points={bluePoints}
				color="#2adfff"
				lineWidth={1.2}
				transparent
				opacity={0.4}
			/>
			{whitePoints.map((point, index) => (
				<mesh key={`white-point-${index}`} position={point}>
					<sphereGeometry args={[0.085, 18, 18]} />
					<meshBasicMaterial
						color="#ffffff"
						transparent
						opacity={0.98}
					/>
				</mesh>
			))}
			{bluePoints.map((point, index) => (
				<mesh key={`blue-point-${index}`} position={point}>
					<sphereGeometry args={[0.06, 16, 16]} />
					<meshBasicMaterial
						color="#29e3ff"
						transparent
						opacity={0.7}
					/>
				</mesh>
			))}
		</group>
	);
}

function MarketGlobe() {
	const globeRef = useRef();
	const continentPaths = useMemo(
		() => [
			[
				[-0.9, 1.05, 3.05],
				[-1.4, 0.68, 3.1],
				[-1.55, 0.1, 3.08],
				[-1.15, -0.45, 3.02],
				[-0.7, -1.05, 2.92],
				[-0.08, -1.38, 2.86],
				[0.34, -0.88, 2.84],
				[0.15, -0.12, 2.82],
				[0.52, 0.62, 2.78],
				[0.06, 1.08, 2.8],
				[-0.9, 1.05, 3.05],
			],
			[
				[-0.12, 1.48, 2.76],
				[0.58, 1.64, 2.72],
				[1.05, 1.35, 2.66],
				[1.48, 1.16, 2.58],
				[1.92, 0.92, 2.48],
			],
			[
				[1.48, -0.36, 2.54],
				[1.98, -0.08, 2.42],
				[2.42, 0.12, 2.28],
				[2.78, 0.62, 2.1],
				[3.05, 1.08, 1.88],
			],
		],
		[]
	);

	useFrame((state) => {
		if (!globeRef.current) {
			return;
		}

		globeRef.current.rotation.y = -0.38 + Math.sin(state.clock.elapsedTime * 0.22) * 0.08;
		globeRef.current.rotation.x = -0.08 + Math.cos(state.clock.elapsedTime * 0.16) * 0.03;
	});

	return (
		<group ref={globeRef} position={[7.1, 1.15, -11.2]} scale={1.52}>
			<mesh>
				<sphereGeometry args={[3.45, 48, 48]} />
				<meshBasicMaterial
					color="#0b2f63"
					transparent
					opacity={0.12}
					side={THREE.DoubleSide}
				/>
			</mesh>
			<mesh>
				<sphereGeometry args={[3.48, 26, 26]} />
				<meshBasicMaterial
					color="#169cff"
					wireframe
					transparent
					opacity={0.2}
				/>
			</mesh>
			{[-1.6, -0.8, 0, 0.8, 1.6].map((y, index) => (
				<mesh key={`latitude-${index}`} position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]}>
					<torusGeometry args={[Math.max(1.25, 3.2 - Math.abs(y) * 0.78), 0.016, 10, 96]} />
					<meshBasicMaterial
						color="#1fb8ff"
						transparent
						opacity={0.28}
					/>
				</mesh>
			))}
			{[-1.2, -0.45, 0.35, 1.12].map((rotationY, index) => (
				<mesh key={`longitude-${index}`} rotation={[0, rotationY, Math.PI / 2]}>
					<torusGeometry args={[3.25, 0.014, 10, 96]} />
					<meshBasicMaterial
						color="#22b3ff"
						transparent
						opacity={0.24}
					/>
				</mesh>
			))}
			{continentPaths.map((path, index) => (
				<Line
					key={`continent-${index}`}
					points={path}
					color="#32b6ff"
					lineWidth={1}
					transparent
					opacity={0.72}
				/>
			))}
		</group>
	);
}

function WelcomeOverlayScene() {
	return (
		<>
			<color attach="background" args={["#010713"]} />
			<fog attach="fog" args={["#010713", 9, 32]} />
			<ambientLight intensity={0.65} color="#d7eeff" />
			<pointLight position={[0, 2.6, 7]} intensity={18} distance={24} color="#29dbff" />
			<pointLight position={[8, 1, 4]} intensity={14} distance={22} color="#0c6bff" />
			<spotLight
				position={[-2, 7, 9]}
				angle={0.52}
				penumbra={1}
				intensity={12}
				distance={40}
				color="#9be8ff"
			/>

			<WelcomeParticleField count={620} spread={30} height={14} depth={22} size={0.045} opacity={0.72} />
			<WelcomeParticleField
				count={180}
				spread={18}
				height={8}
				depth={12}
				size={0.082}
				drift={-0.02}
				opacity={0.6}
			/>
			<mesh position={[0, 0.4, -12.5]} scale={[14.5, 7.8, 1]}>
				<planeGeometry args={[1, 1]} />
				<meshBasicMaterial
					color="#072b54"
					transparent
					opacity={0.22}
					depthWrite={false}
					blending={THREE.AdditiveBlending}
				/>
			</mesh>
			<mesh position={[0, -2.6, -7.6]} scale={[15, 0.24, 1]}>
				<planeGeometry args={[1, 1]} />
				<meshBasicMaterial
					color="#1bdcff"
					transparent
					opacity={0.6}
					depthWrite={false}
					blending={THREE.AdditiveBlending}
				/>
			</mesh>
			<MarketGridFloor />
			<MarketCandles />
			<MarketTrendLines />
			<MarketGlobe />
		</>
	);
}

const realStoredValue = (value) => {
	if (!value || value === "null" || value === "undefined") return "";
	return value;
};

const signedInDashboardPath = () => {
	if (realStoredValue(localStorage.getItem("user_id"))) return "/home";
	if (
		realStoredValue(localStorage.getItem("virtualmall_token")) &&
		realStoredValue(localStorage.getItem("virtual_user_id"))
	) {
		return "/user-points";
	}
	return "";
};

const hasMallSession = () => Boolean(signedInDashboardPath());

export default function MyAdvertisements() {
	const [currentView, setCurrentView] = useState("hallway");
	const [selectedStore, setSelectedStore] = useState(null);
	const [cameraPosition, setCameraPosition] = useState([0, 1.6, 10]);
	const [cameraLookAt, setCameraLookAt] = useState([0, 1.6, 0]);
	const [wallDecoration, setWallDecorations] = useState([[]]);
	const [showWelcome, setShowWelcome] = useState(() => !hasMallSession());
	const [showRegistrationForm, setShowRegistrationForm] = useState(false);
	const [Uvideo, setUvideo] = useState("dQw4w9WgXcQ"); // Default video link if none loaded
	const [showButton, setShowButton] = useState(false);
	const [links, setLinks] = useState([]);
	const [Points, setPoints] = useState(null);
	const [whatsappLink, setWhatsappLink] = useState("");

	const navigate = useNavigate();

	const AdVideo = async () => {
		try {
			const response = await axios.get(
				`${APP_BASE_URL}/ads/video`,
				{ status: "single" }
			);

			if (response?.data?.result) {
				console.log(response.data.link, 'link')
				setUvideo(response.data.link);
			} else {
				// alert("Failed to load video");
			}
		} catch (error) {
			console.error(error);
			// alert("Failed to load video");
		}
	};

	const LinkLists = async () => {
		try {
			const response = await axios.post(`${APP_BASE_URL}/vitual_mall/ytlink/list`);
			if (response?.data?.result) {
				setLinks(response?.data?.data);
			} else {
			}
		} catch (error) {
		}
	}

	useEffect(() => {
		AdVideo();
		LinkLists();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(
					`${APP_BASE_URL}/vitual_mall/ads/list`,
					{
						headers: {
							user_id: localStorage.getItem("user_id"),
						},
					}
				);
				setWallDecorations(response.data.data || [[]]);
				setPoints(response.data.user_temp_point);
			} catch (error) {
				console.error("Advertisement error:", error);
				setWallDecorations([[]]);
			}
		};

		const fetchWhatsappLink = async () => {
			try {
				const response = await axios.get(`${APP_BASE_URL}/vitual_mall/whatsapp/active`);
				if (response.data.status && response.data.data) {
					setWhatsappLink(response.data.data.vmwg_link);
				}
			} catch (error) {
				console.error("Error fetching whatsapp link:", error);
			}
		};

		fetchData();
		fetchWhatsappLink();
	}, []);

	const shopList = [
		{
			id: 1,
			name: "SpringWay",
			color: "#ff9999",
			wall: "front",
			position: 1,
		},
		{
			id: 2,
			name: "Explore Shops",
			color: "#ff9999",
			wall: "right",
			position: 1,
		},
		{
			id: 3,
			name: "shop 3",
			color: "#ff9999",
			wall: "left",
			position: 1,
		},
		{
			id: 4,
			name: "shop 4",
			color: "#ff9999",
			wall: "right",
			position: 1,
		},
		{
			id: 5,
			name: "shop 5",
			color: "#ff9999",
			wall: "left",
			position: 1,
		},
		{
			id: 6,
			name: "shop 6",
			color: "#ff9999",
			wall: "right",
			position: 1,
		},
		{
			id: 7,
			name: "shop 7",
			color: "#ff9999",
			wall: "left",
			position: 1,
		},
		{
			id: 8,
			name: "shop 8",
			color: "#ff9999",
			wall: "right",
			position: 1,
		},
		{
			id: 9,
			name: "shop 9",
			color: "#ff9999",
			wall: "left",
			position: 1,
		},
		{
			id: 10,
			name: "shop 10",
			color: "#ff9999",
			wall: "right",
			position: 1,
		},
		{
			id: 11,
			name: "shop 11",
			color: "#ff9999",
			wall: "left",
			position: 1,
		},
		{
			id: 12,
			name: "shop 12",
			color: "#ff9999",
			wall: "right",
			position: 1,
		},
	];
	/* store set navigation removed (not used) */

	const [showVideo, setShowVideo] = useState(false);
	const handleJustWatch = () => {
		setShowWelcome(false);
		setShowVideo(false);
		navigate("/user-points", { state: { previewOnly: true } });
	};
	const handleCloseVideo = () => {
		setShowWelcome(false)
		setShowVideo(false);
		setShowButton(true)
	};

	const enterStore = (store) => {
		setSelectedStore(store);
		setCurrentView("storeRoom");

		if (store.wall === "left") {
			setCameraPosition([-8, 1.6, 0]);
			setCameraLookAt([0, 1.6, 0]);
		} else if (store.wall === "right") {
			setCameraPosition([8, 1.6, 0]);
			setCameraLookAt([0, 1.6, 0]);
		} else {
			setCameraPosition([0, 1.6, 8]);
			setCameraLookAt([0, 1.6, 0]);
		}
	};

	const exitStore = () => {
		setCurrentView("hallway");
		setCameraPosition([0, 1.6, 10]);
		setCameraLookAt([0, 1.6, 0]);
	};

	return (
		<div className="my-ads-shell">
			<ToastContainer />
			{showWelcome && (
				<div className="welcome-overlay">
					<ErrorBoundary fallback={<div className="welcome-overlay__fallback" />}>
						<div className="welcome-overlay__scene" aria-hidden="true">
							<Canvas
								dpr={[1, 1.5]}
								camera={{ position: [0, 0, 12], fov: 46 }}
								gl={{ antialias: true, alpha: true }}
								style={{ pointerEvents: "none" }}
							>
								<WelcomeOverlayScene />
							</Canvas>
						</div>
					</ErrorBoundary>
					<div className="welcome-overlay__gradient" aria-hidden="true" />
					<div className="welcome-overlay__grain" aria-hidden="true" />

					<div className="welcome-card">
						<div className="welcome-card__eyebrow">Springway Virtual Mall</div>
						{showRegistrationForm ? (
							<RegistrationForm onBack={() => setShowRegistrationForm(false)} />
						) : (
							<>
								<h1 className="welcome-card__title">
									Are you springway customer?
								</h1>

								<p className="welcome-card__subtitle">
									Step into a cleaner, more cinematic mall preview before you continue.
								</p>

								<div className="welcome-card__actions">
									<button
										onClick={() => navigate('/virtual_mall_login?redirect=/user-points')}
										className="welcome-card__button welcome-card__button--primary"
									>
										1. Yes
									</button>

									<button
										onClick={() => setShowRegistrationForm(true)}
										className="welcome-card__button welcome-card__button--secondary"
									>
										2. NO
									</button>

									<button
										onClick={handleJustWatch}
										className="welcome-card__button welcome-card__button--tertiary"
									>
										3. Just Watch
									</button>

									{whatsappLink && (
										<button
											onClick={() => window.open(whatsappLink, '_blank')}
											className="welcome-card__button welcome-card__button--whatsapp"
										>
											<img
												src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
												alt="WA"
												style={{ width: '20px', marginRight: '8px' }}
											/>
											Join Watsapp Group
										</button>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			)}

			{showVideo && (
				<div className="video-overlay">
					<iframe
						width="75%"
						height="50%"
						src={`https://www.youtube.com/embed/${Uvideo}?autoplay=1&mute=0`}
						title="YouTube video"
						// frameBorder="0"
						allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
					></iframe>
					<button className="close-video-btn" onClick={handleCloseVideo}>
						X
					</button>
				</div>
			)}
			<div style={{ width: "100%", height: "500px" }}>
				<Canvas
					style={{ width: "100vw", height: "100vh" }}
					camera={{ position: cameraPosition, fov: 60 }}
				>
					<ambientLight intensity={0.7} />
					<spotLight
						position={[0, 5, 0]}
						angle={0.5}
						penumbra={1}
						intensity={1}
						castShadow
					/>
					<Environment preset="apartment" />

					{currentView === "hallway" ? (
						<>
							<OrbitControls
								target={cameraLookAt}
								minPolarAngle={Math.PI / 4}
								maxPolarAngle={Math.PI / 2}
								enablePan={false}
								minDistance={5} // Prevent zooming out too far
								maxDistance={15} // Prevent zooming in too close
							/>
							<Hallway
								// stores={shops}
								wallDecorations={wallDecoration}
								onStoreSelect={enterStore}
								showButton={showButton}
								links={links}
								Points={Points}
							/>
						</>
					) : (
						<>
							<StoreRoom
								store={selectedStore}
								onExit={exitStore}
								shopList={shopList}
							/>
							<OrbitControls
								target={[0, 1.6, 0]}
								minPolarAngle={Math.PI / 4}
								maxPolarAngle={Math.PI / 2}
								enablePan={false}
								minDistance={5} // Prevent zooming out too far
								maxDistance={15} // Prevent zooming in too close
							/>
						</>
					)}
				</Canvas>
			</div>
			{currentView === "hallway" && (
				<>
					{/* prevSet navigation removed */}
					<button
						onClick={() => navigate(signedInDashboardPath() || "/")}
						style={{
							position: "absolute",
							bottom: "20px",
							left: "50%",
							transform: "translateX(-50%)",
							padding: "10px 20px",
							background: "rgba(255,255,255,0.8)",
							border: "none",
							borderRadius: "5px",
							cursor: "pointer",
							fontSize: "16px",
							zIndex: 100,
						}}
					>
						Exit to Home
					</button>
				</>
			)}
		</div>
	);
}

function Hallway({ wallDecorations = [[]], onStoreSelect, showButton, links, Points }) {

	const [currentAdSetIndex, setCurrentAdSetIndex] = useState(0);
	const currentAdSet = wallDecorations[currentAdSetIndex] || [];

	const floorTexture = useMemo(() => {
		// Create canvas for dynamic texture
		const canvas = document.createElement("canvas");
		const textureSize = 512;
		canvas.width = textureSize;
		canvas.height = textureSize;
		const ctx = canvas.getContext("2d");

		// Base color
		ctx.fillStyle = "#f8f8f8";
		ctx.fillRect(0, 0, textureSize, textureSize);

		// Tile pattern
		const tileSize = 64; // Pixels per tile in texture
		ctx.fillStyle = "rgba(200, 200, 200, 0.5)";

		// Create checkerboard pattern
		for (let y = 0; y < textureSize; y += tileSize) {
			for (let x = 0; x < textureSize; x += tileSize) {
				if ((x + y) % (tileSize * 2) === 0) {
					ctx.fillRect(x, y, tileSize, tileSize);
				}
			}
		}

		// Create Three.js texture
		const texture = new THREE.CanvasTexture(canvas);
		texture.wrapS = THREE.RepeatWrapping;
		texture.wrapT = THREE.RepeatWrapping;
		texture.repeat.set(10, 10); // Adjust tiling density
		return texture;
	}, []);

	const handleNext = () => {
		setCurrentAdSetIndex(prev => (prev + 1) % wallDecorations.length);
	};

	const handlePrev = () => {
		setCurrentAdSetIndex(prev => (prev - 1 + wallDecorations.length) % wallDecorations.length);
	};
	return (
		<group><mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow><planeGeometry args={[20, 20, 100, 100]} /><meshStandardMaterial
			map={floorTexture}
			roughness={0.7}
			metalness={0.1}
			color="#ffffff"
		/></mesh><mesh position={[4, 4.5, 0]} rotation={[0, 0, 0]}><boxGeometry args={[2, 0.5, 0.1]} /><meshStandardMaterial color="#8B4513" /></mesh><group position={[-4, 4.5, 0.11]}><SafeText
			color="white"
			anchorX="center"
			anchorY="middle"
			fontSize={0.3}
			maxWidth={1.8}
			lineHeight={1}
			letterSpacing={0.02}
			font="https://fonts.gstatic.com/s/raleway/v14/1Ptrg8zYS_SKggPNwK4vaqI.woff"
		>

		</SafeText></group><group position={[4, 4.5, 0.11]}><SafeText
			color="white"
			anchorX="center"
			anchorY="middle"
			fontSize={0.3}
			maxWidth={1.8}
			lineHeight={1}
			letterSpacing={0.02}
			font="https://fonts.gstatic.com/s/raleway/v14/1Ptrg8zYS_SKggPNwK4vaqI.woff"
		>

		</SafeText></group><MallFurniture links={links} /><Dustbin /><WallDecoration decoration={currentAdSet} />{showButton && (<Html
			as="div"
			wrapperClass="nav-buttons-wrapper"
			style={{
				width: '100%',
				position: 'fixed',
				top: 0,
				left: 0,
				pointerEvents: 'none',
				zIndex: 1000
			}}
		>
			<button
				onClick={handlePrev}
				className="btn btn-light d-flex justify-content-center align-items-center shadow"
				style={{
					position: 'absolute',
					left: '5vw', // Use viewport width for responsiveness
					top: '50%',
					transform: 'translateY(-50%)',
					borderRadius: '50%',
					cursor: 'pointer',
					width: '60px',
					height: '60px',
					pointerEvents: 'auto',
					fontSize: '12px',
					backgroundColor: "white"
				}}
			>
				Prev
			</button>

			<button
				onClick={handleNext}
				className="btn btn-light d-flex justify-content-center align-items-center shadow"
				style={{
					position: 'absolute',
					right: '5vw',
					top: '50%',
					transform: 'translateY(-50%)',
					borderRadius: '50%',
					cursor: 'pointer',
					width: '60px',
					height: '60px',
					pointerEvents: 'auto',
					fontSize: '12px',
					backgroundColor: "white"
				}}
			>
				Next
			</button>
		</Html>
		)}</group>
	);
}

/* StoreEntrance removed (unused) */

function WallDecoration({ decoration }) {
	const leftWallAds = decoration?.filter(ad => ad.wall === "left").slice(0, 2);
	const rightWallAds = decoration?.filter(ad => ad.wall === "right").slice(0, 2);
	const frontWallAds = decoration?.filter(ad => ad.wall === "front").slice(0, 2);

	return (
		<>
			{/* Left Wall Ads */}
			{leftWallAds?.map((decoration, index) => (
				<ErrorBoundary key={`left-${index}`} fallback={null}>
					<React.Suspense fallback={null}>
						<SingleAd decoration={decoration} positionIndex={index} wall="left" />
					</React.Suspense>
				</ErrorBoundary>
			))}

			{/* Right Wall Ads */}
			{rightWallAds?.map((decoration, index) => (
				<ErrorBoundary key={`right-${index}`} fallback={null}>
					<React.Suspense fallback={null}>
						<SingleAd decoration={decoration} positionIndex={index} wall="right" />
					</React.Suspense>
				</ErrorBoundary>
			))}

			{frontWallAds?.map((decoration, index) => (
				<ErrorBoundary key={`front-${index}`} fallback={null}>
					<React.Suspense fallback={null}>
						<SingleAd decoration={decoration} positionIndex={index} wall="front" />
					</React.Suspense>
				</ErrorBoundary>
			))}
		</>
	);
}

function SingleAd({ decoration, positionIndex, wall }) {

	// Guard texture loading - provide a fallback placeholder when image is missing
	const getSafeImageUrl = (url) => {
		if (!url) return 'https://via.placeholder.com/350x250.png?text=No+Image';

		const backendHost = APP_BASE_URL.split('/mlm')[0];

		// Fix for known domains that should point to the current backend
		if (url.includes('macdon360.in:3004') || url.includes('localhost:3004')) {
			url = url.replace(/https?:\/\/macdon360\.in:3004/g, backendHost)
				     .replace(/http:\/\/localhost:3004/g, backendHost);
		}

		return url;
	};

	const texture = useTexture(getSafeImageUrl(decoration?.image));
	const yPos = 2.5;
	const xOffset = 8; // closer to wall edge
	const zGap = 4;
	const zPos = positionIndex * zGap - (zGap / 2);

	let position = [0, yPos, zPos]; // default for front wall
	let rotation = [0, Math.PI, 0]; // facing back to camera

	if (wall === "left") {
		position = [-xOffset, yPos, zPos];
		rotation = [0, Math.PI / 2, 0];
	} else if (wall === "right") {
		position = [xOffset, yPos, zPos];
		rotation = [0, -Math.PI / 2, 0];
	} else if (wall === "front") {
		position = [zPos, yPos, -xOffset];  // push it toward front wall (negative Z)
		rotation = [0, 0, 0];               // face directly forward
	}


	const socket = useRef(null);
	const baseurl = "https://api.springwayhub.com/";
	// const user_id = localStorage.getItem("user_id");

	useEffect(() => {
		if (decoration?.ads_id && !socket.current) {
			// Connect to the socket server
			socket.current = io(baseurl, {
				transports: ["websocket"],
				secure: true,
			});

			console.log("Connecting to socket server...", socket.current);

			// Listen for events
			socket.current.on("connect", () => {
				console.log("Socket connected:", socket.current.id);

				// Emit event with decoration id
				socket.current.emit("join_room", decoration.ads_id); // use a meaningful event name
			});

			socket.current.on(decoration?.ads_id, (message) => {
				console.log("like_sent message received:", message);
				if (message.user_id == localStorage.getItem("user_id")) {
					if (message.result) {
						toast.success(message.message);
					} else {
						toast.error(message.message);
						// Reset liked status if it failed
						if (message.message.includes("limit reached")) {
							setLiked(false);
							setLikeCount((prev) => prev - 1);
						}
					}
				}
			});

			socket.current.on("disconnect", () => {
				console.log("Socket disconnected");
			});
		}

		// Cleanup on unmount or decoration change
		return () => {
			if (socket.current) {
				socket.current.disconnect();
				socket.current = null;
				console.log("Socket cleaned up");
			}
		};
	}, [decoration?.ads_id]);

	const [showModal, setShowModal] = useState(false);
	const [liked, setLiked] = useState(false);
	const [likeCount, setLikeCount] = useState(decoration?.ads_like || 0);
	const [phoneNumber, setPhoneNumber] = useState('');
	const [phoneError, setPhoneError] = useState('');
	const [comment, setComment] = useState('');
	const [uploadModel, setUploadModel] = useState(false);
	const { register, handleSubmit, formState } = useForm();
	const user_id = localStorage.getItem("user_id");
	const [adsId, setAdsId] = useState();

	const handleLike = () => {
		// Prevent double-like
		if (liked) return;

		// Only emit if socket connected
		if (socket.current && socket.current.connected) {
			socket.current.emit("like_sent", {
				a_id: decoration?.ads_id,
				user_id: localStorage.getItem("user_id"),
			});
		} else {
			console.warn('Socket not connected, skipping emit');
		}

		setLiked(true);
		setLikeCount((prev) => (typeof prev === 'number' ? prev + 1 : 1));
	};

	const handleComment = (e) => {
		console.log(comment, phoneNumber, 'phoneNumber')
		e.preventDefault();

		const phoneRegex = /^[0-9]{10}$/; // basic 10-digit validation

		if (phoneNumber && !phoneNumber.match(phoneRegex)) {
			setPhoneError('Please enter a valid 10-digit phone number');
			return; // Stop if invalid
		}

		setPhoneError(''); // Clear any error

		console.log({
			a_id: decoration?.ads_id,
			comment: comment,
			user_id: user_id,
			phone_number: phoneNumber,
		}, '11111')
		socket.current.emit("comment_sent", {
			a_id: decoration?.ads_id,
			comment: comment,
			user_id: user_id,
			phone_number: phoneNumber,
		});
		setShowModal(false);

	};

	const handleDownload = (url) => {
		fetch(url, {
			mode: "cors",
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				return response.blob();
			})
			.then((blob) => {
				const blobUrl = window.URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = blobUrl;
				const fileName = url.split("/").pop();
				a.download = fileName;
				document.body.appendChild(a);
				a.click();
				a.remove();
				window.URL.revokeObjectURL(blobUrl);
			})
			.catch((err) => {
				console.error("Download failed:", err);
				toast.error("Download failed. The file might be missing on the server.");
			});
	};

	// Small inline modal component removed (unused) — using Html/Bootstrap Modal below instead

	const handleCloseModal = () => {
		setUploadModel(false);
	};
	const onSubmit = async (data) => {
		console.log("ad Submitted", data);

		try {

			const formData = new FormData();
			formData.append("image", data.image[0]);
			formData.append("views", data.views);
			formData.append("phone_number", data.phone_number);
			formData.append("a_id", adsId);
			console.log(data, data.image[0], 'formdata')

			const response = await axios.post(
				"https://api.springwayhub.com/virtual_mall/upload",
				formData,
				{
					headers: { user_id: localStorage.getItem("user_id") },
				}
			);
			if (response?.data?.result) {
				setShowModal(false);
				alert("advertisement added successfully")
				window.location.reload();
			} else {
				alert("error when adding advertisement")
			}

			console.log(response.data.result, 'response')
		} catch (error) {
			console.error("Error when adding advertisement", error);
		}
	};
	return (
		<group position={position} rotation={rotation}>
			<mesh onClick={() => setShowModal(true)}>
				<planeGeometry args={[3.5, 2.5]} /> {/* Increased from [2, 1.5] */}
				<meshStandardMaterial
					map={texture}
					roughness={0.5}
					metalness={0.1}
					transparent={true}
					opacity={0.95}
				/>
			</mesh>

			<SafeText
				position={[0, -1.7, 0]} // Just below the ad
				fontSize={0.2}          // Smaller font size
				color="#000"
				anchorX="center"
				anchorY="middle"
			>
				{decoration?.ads_description || "Advertisement"}
			</SafeText>

			<SafeText
				position={[0, -2.1, 0]} // Below the description
				fontSize={0.15}         // Even smaller
				color="#555"
				anchorX="center"
				anchorY="middle"
			>
				{decoration?.ads_location || "Location"}
			</SafeText>


			{showModal && (
				<Html center>
					<div className="position-relative p-4 bg-white rounded shadow-lg" style={{ maxWidth: "700px", margin: "0 auto" }}>
						{/* Close button in top right */}
						<button
							type="button"
							className="btn btn-danger position-absolute top-0 end-0 translate-middle-y rounded-circle d-flex align-items-center justify-content-center"
							style={{
								width: "36px",
								height: "36px",
								zIndex: 10,
								backgroundColor: "#dc3545",
								borderColor: "#dc3545",
								color: "white"
							}}
							onClick={() => setShowModal(false)}
						>
							X
						</button>

						<div className="modal-content-wallDecoration">
							<img
								src={decoration.image}
								alt={decoration?.ads_description || "advertisement"}
								className="img-fluid rounded mb-3"
							/>

							<div className="d-flex flex-wrap justify-content-between align-items-start bg-light p-3 rounded shadow-sm gap-3">
								{/* Like + Download Section */}
								<div className="d-flex flex-column align-items-center">
									<div className="d-flex align-items-center gap-2 mb-2" style={{ cursor: "pointer" }} onClick={() => handleLike()}>
										<span className="text-muted fw-medium">{likeCount}</span>
										<img
											src={(liked || decoration?.like_status) ? like : Dislike}
											alt="like-icon"
											width="40"
											height="40"
										/>
									</div>
									<button
										onClick={() => handleDownload(decoration?.image)}
										className="btn btn-primary btn-sm"
										style={{
											zIndex: 10,
											backgroundColor: "#0d6efd",
											borderColor: "#0d6efd",
											color: "white"
										}}
									>
										Download
									</button>
								</div>

								<div className="text-muted small mt-1">
									Views: <strong>{decoration?.ads_views || 0}</strong>
								</div>
								<div className="text-muted small mt-1">
									Single Points: <strong>{decoration?.ads_single || 0}</strong>
								</div>
								<div className="text-muted small mt-1">
									Total Views: <strong>{decoration?.ads_total || 0}</strong>
								</div>
								{/* Comment + Upload Section */}
								{!user_id ? (
									<div className="flex-grow-1">
										<div className="text-muted small mt-1">
											Phone Number
										</div>
										<input
											type="tel"
											className={`form-control form-control-lg ${phoneError ? 'is-invalid' : ''}`}
											placeholder="Enter phone number to comment"
											value={phoneNumber}
											onChange={(e) => {
												setPhoneNumber(e.target.value);
												setPhoneError(''); // clear error on change
											}}
											style={{ width: "100%" }}
										/>
										{phoneError && <div className="invalid-feedback">{phoneError}</div>}

										<div className="position-relative mb-3">
											<div className="text-muted small mt-1">
												Comment
											</div>
											<input
												type="text"
												className="form-control form-control-lg pe-5"
												placeholder="Add a comment..."
												onChange={(e) => {
													setComment(e.target.value)
												}}
											/>
											<button
												type="button"
												className="btn btn-danger position-absolute top-50 end-0 translate-middle-y rounded-circle"
												style={{
													width: "30px", height: "30px", backgroundColor: "#dc3545",
													borderColor: "#dc3545",
												}}
												onClick={(e) => handleComment(e)}
											>
												➤
											</button>
										</div>
									</div>
								) : (
									<div className="flex-grow-1">
										<div className="position-relative mb-3">
											<div className="text-muted small mt-1">
												Comment
											</div>
											<input
												type="text"
												className="form-control form-control-lg pe-5"
												placeholder="Add a comment..."
												onChange={(e) => {
													setComment(e.target.value)
												}}
											/>
											<button
												type="button"
												className="btn btn-danger position-absolute top-50 end-0 translate-middle-y rounded-circle"
												style={{
													width: "30px", height: "30px", backgroundColor: "#dc3545",
													borderColor: "#dc3545",
												}}
												onClick={(e) => handleComment(e)}
											>
												➤
											</button>
										</div>
									</div>
								)}

								<button className="btn btn-secondary btn-sm"
									style={{
										zIndex: 10,
										backgroundColor: "#0d6efd",
										borderColor: "#0d6efd",
										color: "white"
									}}
									onClick={() => {
										setUploadModel(true)
										setShowModal(false)
										setAdsId(decoration?.ads_id)
									}}>
									Upload
								</button>
							</div>
						</div>
					</div>
				</Html>
			)}

			{uploadModel && <Html center>
				{/* <div className="position-relative p-4 bg-white rounded shadow-lg" style={{ maxWidth: "700px", margin: "0 auto" }}> */}
				<Modal show={uploadModel} onHide={handleCloseModal} centered>
					<Modal.Header closeButton>
						<Modal.Title></Modal.Title>
					</Modal.Header>
					<Modal.Body>
						{(
							<Form onSubmit={handleSubmit(onSubmit)}>
								<Form.Group controlId="referral">
									<Form.Label>Add Advertisement</Form.Label>
									<Form.Control
										type="file"
										accept="image/*"
										{...register("image", {
											required: "Please select an image file",
											validate: {
												isImage: value =>
													value[0]?.type.startsWith("image/") ||
													"Only image files are allowed"
											},
										})}
										onChange={() => { /* image file handled by register */ }}
										isInvalid={!!formState.errors.image}
									/>
									<Form.Control.Feedback type="invalid">
										{formState.errors.image?.message}
									</Form.Control.Feedback>
								</Form.Group>
								<Form.Group controlId="title" className="mt-3">
									<Form.Label>Views Count</Form.Label>
									<Form.Control
										type="number"
										placeholder="Enter Views Count"
										{...register("views", {
											required: "views count is required",
											// maxLength: {
											// 	value: 100,
											// 	message: "views count should be less than 100 characters",
											// },
										})}
										isInvalid={!!formState.errors.title}
									/>
									<Form.Control.Feedback type="invalid">
										{formState.errors.title?.message}
									</Form.Control.Feedback>
								</Form.Group>

								{/* Text Field 2: Description */}
								<Form.Group controlId="phone_number" className="mt-3">
									<Form.Label>Phone Number</Form.Label>
									<Form.Control
										type="tel"
										placeholder="Enter phone number"
										{...register("phone_number", {
											required: "Phone number is required",
											pattern: {
												value: /^[0-9]{10}$/, // Only digits, exactly 10 characters
												message: "Phone number must be 10 digits"
											}
										})}
										isInvalid={!!formState.errors.phone_number}
									/>
									<Form.Control.Feedback type="invalid">
										{formState.errors.phone_number?.message}
									</Form.Control.Feedback>
								</Form.Group>

								<Button variant="primary" type="submit" className="mt-3">
									Upload
								</Button>
							</Form>
						)}
					</Modal.Body>
				</Modal>
				{/* </div> */}
			</Html>
			}
		</group >
	);
}

function StoreRoom({ store, onExit, shopList }) {
	const [doorOpen, setDoorOpen] = useState(false);
	const doorRef = useRef();

	console.log(store, "store", shopList);
	// Get products from the store data
	const products = store.products.map((product, index) => ({
		id: index + 1,
		name: product.name,
		price: product.price,
		imageUrl: product.image,
		size: [1.5, 1.5, 0.1],
	}));

	// Determine which wall is the "feature wall" (with store branding)
	const featureWall = store.wall;

	// Wall positions and rotations
	const wallConfigs = {
		left: {
			position: [-10, 2.5, 0],
			rotation: [0, Math.PI / 2, 0],
			exitPosition: [-9.9, 1, 0],
			exitRotation: [0, Math.PI / 2, 0],
		},
		right: {
			position: [10, 2.5, 0],
			rotation: [0, -Math.PI / 2, 0],
			exitPosition: [9.9, 1, 0],
			exitRotation: [0, -Math.PI / 2, 0],
		},
		front: {
			position: [0, 2.5, -10],
			rotation: [0, 0, 0],
			exitPosition: [0, 1, -9.9],
			exitRotation: [0, 0, 0],
		},
	};

	// Wall colors (darker for feature wall)
	const wallColors = {
		left: "#e8e8e8",
		right: "#e0e0e0",
		front: "#d8d8d8",
		feature: store.color,
	};

	// Load product textures
	const productTextures = useLoader(
		THREE.TextureLoader,
		products.map((p) => p.imageUrl)
	);

	// Create tile texture
	const tileTexture = useMemo(() => {
		const canvas = document.createElement("canvas");
		canvas.width = 512;
		canvas.height = 512;
		const ctx = canvas.getContext("2d");

		// Base tile color
		ctx.fillStyle = "#ffffff";
		ctx.fillRect(0, 0, 512, 512);

		// Grout lines
		ctx.strokeStyle = "#e0e0e0";
		ctx.lineWidth = 4;

		// Vertical lines
		for (let x = 0; x < 512; x += 64) {
			ctx.beginPath();
			ctx.moveTo(x, 0);
			ctx.lineTo(x, 512);
			ctx.stroke();
		}

		// Horizontal lines
		for (let y = 0; y < 512; y += 64) {
			ctx.beginPath();
			ctx.moveTo(0, y);
			ctx.lineTo(512, y);
			ctx.stroke();
		}

		const texture = new THREE.CanvasTexture(canvas);
		texture.wrapS = THREE.RepeatWrapping;
		texture.wrapT = THREE.RepeatWrapping;
		texture.repeat.set(10, 10);
		return texture;
	}, []);

	// Animate door opening
	useFrame(() => {
		if (doorRef.current) {
			const targetRotation = doorOpen ? Math.PI / 2 : 0;
			doorRef.current.rotation.y = THREE.MathUtils.lerp(
				doorRef.current.rotation.y,
				targetRotation,
				0.1
			);
		}
	});

	const handleDoorClick = () => {
		setDoorOpen(true);
		setTimeout(() => {
			onExit();
			setDoorOpen(false);
		}, 1000);
	};

	// Position products in a single line along the feature wall
	const getProductPosition = (index) => {
		const spacing = 3.5;
		const startPos = -((products.length - 1) * spacing) / 2;
		return [startPos + index * spacing, 1.5, 0.11];
	};
	const [shopPage, setShopPage] = useState(0);
	const shopsPerWall = 3;

	const leftShops = shopList.filter((s) => s.wall === "left");
	const rightShops = shopList.filter((s) => s.wall === "right");

	const totalPages = Math.ceil(
		Math.max(leftShops.length, rightShops.length) / shopsPerWall
	);

	const shopsOnWalls = useMemo(
		() => ({
			left: leftShops.slice(
				shopPage * shopsPerWall,
				(shopPage + 1) * shopsPerWall
			),
			right: rightShops.slice(
				shopPage * shopsPerWall,
				(shopPage + 1) * shopsPerWall
			),
		}),
		[shopPage, leftShops, rightShops]
	);

	const getShopPosition = (index, wall) => {
		const horizontalSpacing = 3;
		const startX = -((shopsOnWalls[wall].length - 1) * horizontalSpacing) / 2;

		return [startX + index * horizontalSpacing, 1.5, 0.11]; // 0.11 pushes the shop slightly in front of the wall
	};

	const [selectedShop, setSelectedShop] = useState(null);
	const [passwordInput, setPasswordInput] = useState("");
	const [enterRoom, setEnterRoom] = useState(false);

	const handleEnterClick = () => {
		console.log("Entered password:", passwordInput);
		// Add your logic here
		setEnterRoom(true);
	};

	const [showAddProductPopup, setShowAddProductPopup] = useState(false);
	const [productName, setProductName] = useState("");
	const [productPrice, setProductPrice] = useState("");
	const [productImage, setProductImage] = useState("");

	return (
		<group>
			{/* Tiled Floor */}
			<mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
				<planeGeometry args={[20, 20, 1, 1]} />
				<meshStandardMaterial
					map={tileTexture}
					color="#f5f5f5"
					roughness={0.2}
				/>
			</mesh>

			{/* Left and Right walls (without shops) */}
			{["left", "right"].map((wall) => (
				<group key={wall}>
					{/* Main wall */}
					<mesh
						position={wallConfigs[wall].position}
						rotation={wallConfigs[wall].rotation}
					>
						<boxGeometry args={[20, 5, 0.2]} />
						<meshStandardMaterial
							color={
								wall === featureWall ? wallColors.feature : wallColors[wall]
							}
						/>
					</mesh>
				</group>
			))}

			{store.wall === "front" &&
				["left", "right"].map((wall) => (
					<group
						key={`shops-${wall}`}
						position={wallConfigs[wall].position}
						rotation={wallConfigs[wall].rotation}
					>
						{shopsOnWalls[wall].map((shop, index) => (
							<group
								key={`shop-${shop.id}`}
								position={getShopPosition(index, wall)}
								onClick={() => setSelectedShop(shop)}
								onPointerOver={() => (document.body.style.cursor = "pointer")}
								onPointerOut={() => (document.body.style.cursor = "auto")}
							>
								{/* Shop Tile */}
								<mesh>
									<boxGeometry args={[1.5, 1, 0.1]} />
									<meshStandardMaterial color={shop.color} />
								</mesh>

								{/* Shop Name */}
								<SafeText
									position={[0, 0, 0.11]}
									fontSize={0.25}
									anchorX="center"
									anchorY="middle"
									color="#000"
								>
									{shop.name}
								</SafeText>
							</group>
						))}
					</group>
				))}

			{/* Prev/Next Buttons */}
			{store.wall === "front" && (
				<>
					{/* Prev Button */}
					<group
						position={[-4.5, 1, -9.7]}
						onClick={() => setShopPage((prev) => Math.max(prev - 1, 0))}
						onPointerOver={() => (document.body.style.cursor = "pointer")}
						onPointerOut={() => (document.body.style.cursor = "auto")}
					>
						<mesh>
							<boxGeometry args={[1.2, 0.6, 0.2]} />
							<meshStandardMaterial
								color={shopPage === 0 ? "#111111" : "#333333"}
							/>
						</mesh>
						<SafeText
							fontSize={0.25}
							position={[0, 0, 0.15]}
							color="#fff"
							anchorX="center"
							anchorY="middle"
						>
							⬅ Prev
						</SafeText>
					</group>

					{/* Next Button */}
					<group
						position={[4.5, 1, -9.7]}
						onClick={() => {
							if (shopPage + 1 < totalPages) setShopPage((prev) => prev + 1);
						}}
						onPointerOver={() => (document.body.style.cursor = "pointer")}
						onPointerOut={() => (document.body.style.cursor = "auto")}
					>
						<mesh>
							<boxGeometry args={[1.2, 0.6, 0.2]} />
							<meshStandardMaterial
								color={shopPage + 1 >= totalPages ? "#111111" : "#333333"}
							/>
						</mesh>
						<SafeText
							fontSize={0.25}
							position={[0, 0, 0.15]}
							color="#fff"
							anchorX="center"
							anchorY="middle"
						>
							Next ➡
						</SafeText>
					</group>
				</>
			)}

			{selectedShop && !enterRoom && (
				<group position={[0, 1.5, -5]}>
					{/* Popup Box */}
					<mesh>
						<boxGeometry args={[4, 3, 0.2]} />
						<meshStandardMaterial color="#ffffff" />
					</mesh>

					{/* Shop Title */}
					<SafeText
						position={[0, 1, 0.15]}
						fontSize={0.4}
						anchorX="center"
						anchorY="middle"
						color="#000"
					>
						{selectedShop.name}
					</SafeText>

					{/* Input Field Background */}
					<mesh position={[0, 0, 0.15]}>
						<planeGeometry args={[2.5, 0.5]} />
						<meshStandardMaterial color="#eee" />
					</mesh>

					{/* Password Input */}
					<Html position={[0, 0, 0.2]} transform>
						<input
							type="password"
							placeholder="Enter password"
							value={passwordInput}
							onChange={(e) => setPasswordInput(e.target.value)}
							style={{
								width: "200px",
								height: "30px",
								fontSize: "16px",
								padding: "5px",
								borderRadius: "5px",
								border: "1px solid #ccc",
								textAlign: "center",
							}}
						/>
					</Html>

					{/* Enter Button */}
					<Html position={[0, -0.8, 0.2]} transform>
						<button
							onClick={() => {
								handleEnterClick(passwordInput);
								// Add logic to check/store/transition etc.
							}}
							style={{
								padding: "8px 16px",
								fontSize: "16px",
								borderRadius: "5px",
								backgroundColor: "#4CAF50",
								color: "#fff",
								border: "none",
								cursor: "pointer",
							}}
						>
							Enter
						</button>
					</Html>

					{/* Close Button */}
					<Html position={[1.8, 1.2, 0.2]} transform>
						<button
							onClick={() => {
								setSelectedShop(null);
								setPasswordInput("");
							}}
							style={{
								fontSize: "14px",
								backgroundColor: "#f44336",
								color: "#fff",
								border: "none",
								borderRadius: "50%",
								width: "24px",
								height: "24px",
								cursor: "pointer",
								textAlign: "center",
								lineHeight: "24px",
								padding: 0,
							}}
						>
							×
						</button>
					</Html>
				</group>
			)}
			<group>
				{enterRoom && (
					<group>
						{/* Ambient Light */}
						<ambientLight intensity={0.5} />

						{/* Point Light for illumination */}
						<pointLight
							position={[0, 5, 0]}
							intensity={1}
							distance={15}
							decay={2}
						/>

						{/* Room Box */}
						<mesh position={[0, 2.5, 0]}>
							<boxGeometry args={[10, 5, 10]} />
							<meshStandardMaterial color="#2a2a2a" side={THREE.BackSide} />
						</mesh>

						{/* Floor */}
						<mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
							<planeGeometry args={[10, 10]} />
							<meshStandardMaterial color="#3a3a3a" />
						</mesh>

						{/* Add Product Button */}
						<group
							position={[-2, 1, -4]}
							onClick={() => {
								setShowAddProductPopup(true);
							}}
							onPointerOver={() => (document.body.style.cursor = "pointer")}
							onPointerOut={() => (document.body.style.cursor = "auto")}
						>
							<mesh>
								<boxGeometry args={[2, 1, 0.2]} />
								<meshStandardMaterial color="#4CAF50" />
							</mesh>
							<SafeText
								position={[0, 0, 0.15]}
								fontSize={0.3}
								color="#fff"
								anchorX="center"
								anchorY="middle"
							>
								+ Add Product
							</SafeText>
						</group>

						{/* Exit Room Button */}
						<group
							position={[2, 1, -4]}
							onClick={() => setEnterRoom(false)}
							onPointerOver={() => (document.body.style.cursor = "pointer")}
							onPointerOut={() => (document.body.style.cursor = "auto")}
						>
							<mesh>
								<boxGeometry args={[2, 1, 0.2]} />
								<meshStandardMaterial color="#f44336" />
							</mesh>
							<SafeText
								position={[0, 0, 0.15]}
								fontSize={0.3}
								color="#fff"
								anchorX="center"
								anchorY="middle"
							>
								Exit Room
							</SafeText>
						</group>
					</group>
				)}
			</group>

			{showAddProductPopup && (
				<group position={[0, 2, -3]}>
					{/* Background box */}
					<mesh>
						<boxGeometry args={[6, 7, 0.2]} />
						<meshStandardMaterial color="#ffffff" />
					</mesh>

					{/* Form Fields - Push forward & add inner padding */}
					<Html position={[0, 1.2, 0.1]} transform>
						{" "}
						{/* z = 0.3 adds distance from the box */}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: "20px",
								padding: "24px", // adds space inside the container
								backgroundColor: "transparent", // mesh behind is visible
								width: "100%", // optional, just for better layout
								boxSizing: "border-box",
							}}
						>
							{/* Product Name */}
							<input
								type="text"
								placeholder="Product Name"
								value={productName}
								onChange={(e) => setProductName(e.target.value)}
								style={{
									width: "200px",
									padding: "8px",
									fontSize: "14px",
									borderRadius: "4px",
									border: "1px solid #ccc",
								}}
							/>

							{/* Product Price */}
							<input
								type="number"
								placeholder="Price"
								value={productPrice}
								onChange={(e) => setProductPrice(e.target.value)}
								style={{
									width: "200px",
									padding: "8px",
									fontSize: "14px",
									borderRadius: "4px",
									border: "1px solid #ccc",
								}}
							/>

							{/* Image Upload */}
							<input
								type="file"
								accept="image/*"
								onChange={(e) => {
									const file = e.target.files[0];
									if (file) {
										const imageUrl = URL.createObjectURL(file);
										setProductImage(imageUrl);
									}
								}}
								style={{
									width: "200px",
									padding: "8px",
									fontSize: "14px",
									borderRadius: "4px",
									border: "1px solid #ccc",
								}}
							/>

							{/* Image Preview */}
							{productImage && (
								<img
									src={productImage}
									alt="Product Preview"
									style={{
										width: "100px",
										height: "100px",
										objectFit: "cover",
										borderRadius: "6px",
										border: "1px solid #ccc",
									}}
								/>
							)}

							{/* Buttons */}
							<div style={{ display: "flex", gap: "20px" }}>
								<button
									onClick={() => {
										console.log({ productName, productPrice, productImage });
										setShowAddProductPopup(false);
										setProductName("");
										setProductPrice("");
										setProductImage("");
									}}
									style={{
										padding: "8px 16px",
										fontSize: "14px",
										borderRadius: "4px",
										backgroundColor: "#4CAF50",
										color: "#fff",
										border: "none",
										cursor: "pointer",
									}}
								>
									Save
								</button>

								<button
									onClick={() => {
										setShowAddProductPopup(false);
										setProductName("");
										setProductPrice("");
										setProductImage("");
									}}
									style={{
										padding: "8px 16px",
										fontSize: "14px",
										borderRadius: "4px",
										backgroundColor: "#f44336",
										color: "#fff",
										border: "none",
										cursor: "pointer",
									}}
								>
									Cancel
								</button>
							</div>
						</div>
					</Html>
				</group>
			)}

			{/* Front wall (with shops) */}
			<group>
				<mesh
					position={wallConfigs.front.position}
					rotation={wallConfigs.front.rotation}
				>
					<boxGeometry args={[20, 5, 0.2]} />
					<meshStandardMaterial color={wallColors.front} />
				</mesh>
			</group>

			{/* Products only appear on left or right feature walls */}
			{featureWall !== "front" && (
				<group
					position={wallConfigs[featureWall].position}
					rotation={wallConfigs[featureWall].rotation}
				>
					{products.map((product, index) => (
						<group
							key={`product-${product.id}`}
							position={getProductPosition(index)}
						>
							{/* Product Image */}
							<mesh>
								<planeGeometry args={product.size.slice(0, 2)} />
								<meshStandardMaterial
									map={productTextures[index]}
									transparent={true}
									side={THREE.DoubleSide}
								/>
							</mesh>

							{/* Product Frame */}
							<mesh position={[0, 0, -0.05]}>
								<boxGeometry
									args={[product.size[0] * 1.05, product.size[1] * 1.05, 0.1]}
								/>
								<meshStandardMaterial color="#ffffff" />
							</mesh>

							{/* Product Info */}
							<SafeText
								position={[0, -product.size[1] / 2 - 0.2, 0.12]}
								color="black"
								fontSize={0.3}
								anchorX="center"
								anchorY="top"
								font="https://fonts.gstatic.com/s/raleway/v14/1Ptrg8zYS_SKggPNwK4vaqI.woff"
							>
								{product.name}
							</SafeText>
							<SafeText
								position={[0, -product.size[1] / 2 - 0.4, 0.12]}
								color="#333333"
								fontSize={0.25}
								anchorX="center"
								anchorY="top"
								font="https://fonts.gstatic.com/s/raleway/v14/1Ptrg8zYS_SKggPNwK4vaqI.woff"
							>
								${product.price.toFixed(2)}
							</SafeText>
						</group>
					))}
				</group>
			)}

			{/* Ceiling with recessed lights */}
			<group position={[0, 5, 0]}>
				<mesh>
					<boxGeometry args={[20, 0.2, 20]} />
					<meshStandardMaterial color="#f0f0f0" />
				</mesh>
			</group>

			{/* Store Display Elements (only on feature wall) */}
			<group
				position={wallConfigs[featureWall].position}
				rotation={wallConfigs[featureWall].rotation}
			>
				{/* Store Title */}
				<SafeText
					position={[0, 4, 0.1]}
					color="black"
					fontSize={0.5}
					anchorX="center"
					anchorY="middle"
					font="https://fonts.gstatic.com/s/raleway/v14/1Ptrg8zYS_SKggPNwK4vaqI.woff"
					style={{ textShadow: '0 1px 0 #fff' }}
				>
					{store.name}
				</SafeText>
			</group>

			{/* Exit Door (always on front wall) */}
			<group
				position={wallConfigs.front.exitPosition}
				rotation={wallConfigs.front.exitRotation}
				onClick={handleDoorClick}
				onPointerOver={() => (document.body.style.cursor = "pointer")}
				onPointerOut={() => (document.body.style.cursor = "auto")}
			>
				{/* Door frame */}
				<mesh position={[0, 1.5, 0.05]}>
					<boxGeometry args={[2.2, 3.2, 0.1]} />
					<meshStandardMaterial color="#5a3921" />
				</mesh>

				{/* Door */}
				<group ref={doorRef} position={[0, 1.5, 0.1]}>
					<mesh position={[0, 0, 0]}>
						<boxGeometry args={[2, 3, 0.1]} />
						<meshStandardMaterial color="#8b4513" />
					</mesh>
					<mesh position={[0.9, 0, 0.05]}>
						<sphereGeometry args={[0.08, 16, 16]} />
						<meshStandardMaterial
							color="#d4af37"
							metalness={0.9}
							roughness={0.2}
						/>
					</mesh>
				</group>

				{/* Simple Exit sign */}
				<SafeText
					position={[0, 3.2, 0.15]}
					color="#ff0000"
					fontSize={0.3}
					anchorX="center"
					anchorY="middle"
				>
					EXIT
				</SafeText>
			</group>
		</group>
	);
}

function Dustbin() {
	return (
		<group position={[-9.5, 0, -7.9]}>
			{/* Bin body */}
			<mesh>
				<cylinderGeometry args={[0.25, 0.25, 0.6, 32]} />
				<meshStandardMaterial color="#555" roughness={0.6} metalness={0.2} />
			</mesh>

			{/* Rim */}
			<mesh position={[0, 0.3, 0]}>
				<torusGeometry args={[0.26, 0.03, 16, 100]} />
				<meshStandardMaterial color="#888" />
			</mesh>
		</group>
	);
}

function SeatingGroup({ position, links }) {
	return (
		<group position={position}>
			<group position={[0, 0.4, 0]} rotation={[0, Math.PI / 2, 0]}>
				<mesh>
					<boxGeometry args={[2, 0.8, 1]} />
					<meshStandardMaterial color="#c0a080" roughness={0.8} metalness={0.1} />
				</mesh>
			</group>
			{[-1.5, 1.5].map((x, i) => (
				<group key={x} position={[x, 0, -1.2]}>
					<mesh>
						<boxGeometry args={[0.8, 0.6, 0.8]} />
						<meshStandardMaterial color="#8b5e3c" />
					</mesh>
				</group>
			))}
		</group>
	);
}

function MallFurniture({ links }) {
	return (
		<group>
			{[-6, -2, 2, 6].map((z) => (
				<SeatingGroup key={z} position={[0, 0, z]} links={links} />
			))}
		</group>
	);
}
